import { useEffect } from "react";
import { getList } from "../../api/generics";
import TaxPayer from "../../models/tax-payer";
import { useDispatch, useSelector, useStore } from "react-redux";
import { RootState } from "../../store";
import {
  setTaxPayer,
  setTaxPayerErrorLoading,
  setTaxPayerLoading,
  setUserTaxPayers,
} from "../../redux/tax-payer/tax-payer-slice";
import { SUCCESS } from "../../utils/constants/tags";
import SmartdocLoading from "../indicators/SmartdocLoading";
import Error from "../error/Error";
import TaxPayerSuspended from "../tax-payer/TaxPayerSuspended";
import {
  loadSelectedTaxPayer,
  rememberSelectedTaxPayer,
} from "../../utils/tax-payer-selection";

const TAX_PAYER_LIMIT = 1000;

interface AppDataLoadedFirstProps {
  children: React.ReactNode;
}

const AppDataLoadedFirst: React.FC<AppDataLoadedFirstProps> = ({
  children,
}) => {
  const dispatch = useDispatch();
  const store = useStore<RootState>();
  const userId = useSelector((state: RootState) => state.user.id);

  const taxPayerLoaded = useSelector(
    (state: RootState) => state.taxPayer.loaded,
  );

  const taxPayerPermissionsLoading = useSelector(
    (state: RootState) =>
      !state.taxPayerPermissions.loaded ||
      state.taxPayerPermissions.taxPayerId !== state.taxPayer.data.taxPayer.id,
  );

  const taxPayerParametersLoading = useSelector(
    (state: RootState) =>
      !state.taxPayerParameters.loaded ||
      state.taxPayerParameters.taxPayerId !== state.taxPayer.data.taxPayer.id,
  );

  const taxPayerError = useSelector((state: RootState) => state.taxPayer.error);

  const accessBlocked = useSelector(
    (state: RootState) => state.taxPayer.data.taxPayer.accessBlocked === true,
  );

  useEffect(() => {
    if (taxPayerLoaded || !userId) return;
    let active = true;
    const isCurrent = () => active && store.getState().user.id === userId;
    const loadData = async () => {
      dispatch(setTaxPayerLoading(true));
      dispatch(setTaxPayerErrorLoading(false));
      try {
        const result = await getList<TaxPayer>(
          "/tax_payers/for_user/",
          TAX_PAYER_LIMIT,
          0,
        );
        if (!isCurrent()) return;
        if (result.status !== SUCCESS || !result.data) {
          dispatch(setTaxPayerErrorLoading(true));
          return;
        }
        const selected = loadSelectedTaxPayer(userId, result.data.items);
        if (!selected || selected.id === undefined) {
          dispatch(setTaxPayerErrorLoading(true));
          return;
        }
        rememberSelectedTaxPayer(userId, selected.id);
        dispatch(setUserTaxPayers(result.data.items));
        dispatch(setTaxPayer(selected));
      } catch {
        if (isCurrent()) dispatch(setTaxPayerErrorLoading(true));
      } finally {
        if (isCurrent()) dispatch(setTaxPayerLoading(false));
      }
    };
    loadData();
    return () => {
      active = false;
    };
  }, [dispatch, store, userId, taxPayerLoaded]);

  if (taxPayerError) return <Error />;

  if (!taxPayerLoaded) {
    return <SmartdocLoading />;
  }

  // The company's permissions and parameters are refused while it is
  // suspended, so there is nothing to wait for.
  if (accessBlocked) {
    return <TaxPayerSuspended />;
  }

  if (taxPayerPermissionsLoading) {
    return <SmartdocLoading />;
  }

  if (taxPayerParametersLoading) {
    return <SmartdocLoading />;
  }

  return <>{children}</>;
};

export default AppDataLoadedFirst;
